// @ts-nocheck
// Telas auxiliares: Analytics (painel do líder) + Share modal.
import React, { useState, useEffect } from 'react';
import { Icon } from '../icons';
import { Flame } from '../common';
import { toast } from '../toast';
import { useTerm, Avatar, UsersIcon } from './components';
import { CLUBS } from './data';
import { api } from '../../services/apiV2';

const DAY_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']; // dom..sáb (JS getDay)

export const ClubAnalyticsScreen: React.FC<{
  term?: 'clube' | 'grupo';
  onBack?: () => void;
  session?: any;
  clubId?: string;
  clubName?: string;
  onOpenProfile?: (uid: string) => void;
}> = ({ term = 'clube', onBack, session, clubId, clubName, onOpenProfile }) => {
  const T = useTerm(term);
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pokingId, setPokingId] = useState<string | null>(null);
  const [pokedIds, setPokedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!session || !clubId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api.getClubAnalytics(session, clubId);
        if (!cancelled) setData(r);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Erro ao carregar painel');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session, clubId]);

  const handlePoke = async (toUserId: string, name: string) => {
    if (!session || !clubId || pokingId) return;
    setPokingId(toUserId);
    try {
      await api.createPoke(session, clubId, {
        toUserId,
        type: 'pause-noticed',
        message: `${name}, sentimos sua falta no estudo!`,
      });
      setPokedIds(prev => new Set([...prev, toUserId]));
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao cutucar');
    } finally {
      setPokingId(null);
    }
  };

  const fmtNum = (n: number) => (n || 0).toLocaleString('pt-BR');

  return (
    <div className="scroll" style={{ paddingBottom: 110 }}>
      <div style={{ padding: '58px 20px 0' }}>
        <button onClick={onBack} style={{
          width: 38, height: 38, borderRadius: '50%',
          background: 'var(--paper-2)', border: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
        }}>
          <Icon name="chevron-left" size={18}/>
        </button>
        <div className="mono" style={{
          fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ember)',
        }}>
          ✦ Painel do líder
        </div>
        <div className="serif" style={{
          fontSize: 32, lineHeight: 1.05, marginTop: 8, letterSpacing: '-0.01em',
        }}>
          Insights do seu<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>{T.s}</span>.
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
          {clubName || ''}{data ? ` · ${data.members?.total || 0} ${(data.members?.total || 0) === 1 ? 'membro' : 'membros'}` : ''}
        </div>
      </div>

      {loading && !data && (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
          Carregando dados…
        </div>
      )}
      {error && (
        <div style={{
          margin: '20px', padding: 14, borderRadius: 12,
          background: 'var(--ember-soft)', border: '1px solid var(--ember)',
          fontSize: 13, color: 'var(--ember)',
        }}>{error}</div>
      )}

      {data && (
      <>
      {/* Stats principais */}
      <div style={{ padding: '24px 20px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <BigStat
          label="Membros"
          main={fmtNum(data.members.total)}
          sub={<span><span style={{ color: 'var(--sage)' }}>{data.members.active7d} ativos 7d</span>{data.members.inactive30d > 0 ? <> · {data.members.inactive30d} inativos</> : null}</span>}
        />
        <BigStat
          label="Páginas (semana)"
          main={fmtNum(data.reading.weekPagesRead)}
          sub={
            data.reading.prevWeekPagesRead > 0 ? (
              <span style={{ color: data.reading.weekVsPrevPct >= 0 ? 'var(--sage)' : 'var(--ember)' }}>
                {data.reading.weekVsPrevPct >= 0 ? '+' : ''}{data.reading.weekVsPrevPct}% vs anterior
              </span>
            ) : <span style={{ color: 'var(--ink-3)' }}>sem dados anteriores</span>
          }
        />
        <BigStat
          label="Velocidade média"
          main={String(data.reading.avgVelocity)}
          suffix="pág/dia"
          sub="por membro ativo"
        />
        <BigStat
          label="Engajamento"
          main={fmtNum(data.engagement.posts30d)}
          suffix="posts/30d"
          sub={<span>{fmtNum(data.engagement.reactionsTotal)} reações · {fmtNum(data.engagement.commentsTotal)} comentários</span>}
        />
      </div>

      {/* Heatmaps de leitura */}
      <div className="section-head">
        <div>
          <div className="label">Engajamento de leitura</div>
          <div className="serif title" style={{ marginTop: 4 }}>Quando se lê</div>
        </div>
      </div>
      <div style={{ padding: '0 20px' }}>
        <div className="card" style={{ padding: 18 }}>
          <div className="mono" style={{
            fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
            color: 'var(--ink-3)', marginBottom: 10,
          }}>
            Dias da semana (semana atual)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
            {DAY_LABELS.map((d, i) => {
              const v = data.heatmap.byDayOfWeek[i] || 0;
              return (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    width: '100%', height: 36, borderRadius: 6,
                    background: v > 0
                      ? `oklch(${0.92 - v * 0.45} ${0.05 + v * 0.13} 40)`
                      : 'var(--paper-3)',
                  }}/>
                  <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>{d}</span>
                </div>
              );
            })}
          </div>

          <div className="mono" style={{
            fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
            color: 'var(--ink-3)', marginTop: 18, marginBottom: 10,
          }}>
            Horas do dia (semana atual)
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 60 }}>
            {data.heatmap.byHour.map((v: number, i: number) => (
              <div key={i} style={{
                flex: 1, height: `${Math.max(v * 100, 4)}%`,
                background: v > 0.7 ? 'var(--ember)' : v > 0.4 ? 'var(--ember-2)' : v > 0 ? 'var(--paper-3)' : 'transparent',
                borderRadius: 1.5,
              }}/>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span className="mono" style={{ fontSize: 9, color: 'var(--ink-4)' }}>00h</span>
            <span className="mono" style={{ fontSize: 9, color: 'var(--ink-4)' }}>12h</span>
            <span className="mono" style={{ fontSize: 9, color: 'var(--ink-4)' }}>23h</span>
          </div>
        </div>
      </div>

      {/* Membros em risco */}
      <div className="section-head">
        <div className="serif title">Membros em risco</div>
        <span className="label" style={{ color: 'var(--ember)' }}>{data.atRisk.length}</span>
      </div>
      <div style={{ padding: '0 20px' }}>
        {data.atRisk.length === 0 ? (
          <div style={{
            padding: 24, textAlign: 'center',
            border: '1px dashed var(--line-strong)', borderRadius: 14,
            color: 'var(--ink-3)', fontSize: 13,
          }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>🌱</div>
            <div className="serif" style={{ fontSize: 16, fontStyle: 'italic' }}>
              Ninguém em risco
            </div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              Todos os membros leram nos últimos 7 dias.
            </div>
          </div>
        ) : (
          <div style={{ background: 'var(--paper-2)', borderRadius: 18, border: '1px solid var(--line)', overflow: 'hidden' }}>
            {data.atRisk.map((m: any, i: number, arr: any[]) => {
              const wasPoked = pokedIds.has(m.userId);
              const isPoking = pokingId === m.userId;
              return (
                <div key={m.userId} style={{
                  padding: 14, display: 'flex', alignItems: 'center', gap: 12,
                  borderBottom: i < arr.length - 1 ? '1px solid var(--line)' : 'none',
                }}>
                  <span onClick={() => onOpenProfile && m.userId && onOpenProfile(m.userId)} style={{ cursor: onOpenProfile ? 'pointer' : 'default', lineHeight: 0, flexShrink: 0 }}><Avatar letter={m.avatar} size={36} hue={i * 60}/></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div onClick={() => onOpenProfile && m.userId && onOpenProfile(m.userId)} style={{ fontSize: 14, fontWeight: 500, cursor: onOpenProfile ? 'pointer' : 'default' }}>{m.name}</div>
                    <div className="mono" style={{
                      fontSize: 10, color: 'var(--ember)', marginTop: 2, letterSpacing: '0.06em',
                    }}>
                      {m.daysSinceRead >= 999 ? 'sem leituras' : `${m.daysSinceRead} ${m.daysSinceRead === 1 ? 'dia' : 'dias'} sem ler`}
                    </div>
                  </div>
                  <button
                    onClick={() => !wasPoked && handlePoke(m.userId, m.name)}
                    disabled={wasPoked || isPoking}
                    style={{
                      padding: '6px 12px', borderRadius: 999,
                      background: wasPoked ? 'var(--sage)' : 'var(--paper)',
                      border: '1px solid ' + (wasPoked ? 'var(--sage)' : 'var(--line)'),
                      fontSize: 11.5,
                      color: wasPoked ? 'var(--paper)' : 'var(--ink-2)',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      cursor: wasPoked || isPoking ? 'default' : 'pointer',
                      opacity: isPoking ? 0.6 : 1,
                    }}
                  >
                    {wasPoked ? <>✓ cutucado</> : isPoking ? '...' : <><span style={{ fontSize: 13 }}>👋</span> cutucar</>}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Caixas de perguntas em aberto (CTA admin) */}
      {data.engagement.qboxOpen > 0 && (
        <>
          <div className="section-head">
            <div className="serif title">Atenção</div>
          </div>
          <div style={{ padding: '0 20px' }}>
            <div style={{
              padding: 16, borderRadius: 14,
              background: 'color-mix(in oklab, var(--ember) 8%, var(--paper-2))',
              border: '1px solid var(--ember)',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{ fontSize: 26 }}>📥</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  {data.engagement.qboxOpen} {data.engagement.qboxOpen === 1 ? 'pergunta aguardando' : 'perguntas aguardando'} sua resposta
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                  Responda na Caixa de Perguntas pra movimentar o grupo.
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
};

const BigStat: React.FC<{ label: string; main: string; suffix?: string; sub?: React.ReactNode; flame?: boolean }> = ({
  label, main, suffix, sub, flame,
}) => (
  <div className="card" style={{ padding: 16 }}>
    <div className="mono" style={{
      fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase',
      color: 'var(--ink-3)',
    }}>
      {label}
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 6 }}>
      {flame && <Flame count={20} size={22}/>}
      <div className="serif" style={{ fontSize: 30, lineHeight: 1, letterSpacing: '-0.02em' }}>{main}</div>
      {suffix && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{suffix}</span>}
    </div>
    <div style={{ fontSize: 10.5, color: 'var(--ink-4)', marginTop: 6, lineHeight: 1.3 }}>{sub}</div>
  </div>
);

// Modal: Compartilhar/Convidar — gera link real, QR code, lista convites pendentes,
// permite revogar, e oferece compartilhamento nativo + WhatsApp + copiar.
export const ClubShareScreen: React.FC<{
  term?: 'clube' | 'grupo';
  onClose: () => void;
  session?: any;
  clubId?: string;
  clubName?: string;
  clubBookTitle?: string;
}> = ({ term = 'clube', onClose, session, clubId, clubName, clubBookTitle }) => {
  const T = useTerm(term);

  // Convite "ativo" (recém-gerado) com URL pra compartilhar e QR
  const [activeInvite, setActiveInvite] = useState<any | null>(null);
  const [generating, setGenerating] = useState(false);
  // Lista de convites pendentes (não-aceitos) pra admin revogar
  const [pendingInvites, setPendingInvites] = useState<any[] | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  // QR como data URL (gerado via lib qrcode)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  // Estado dos botões CTA
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const url = activeInvite?.url || '';

  const loadInvites = async () => {
    if (!session || !clubId) return;
    setLoadingList(true);
    try {
      const r = await api.listInvites(session, clubId);
      // Backend retorna invites normalizados com flags `pending`, `accepted`,
      // `revoked` derivadas de `status`. Filtra só os usáveis (pending + não-expirados).
      const now = new Date();
      const usable = (r.invites || []).filter((iv: any) => {
        if (iv.pending === false) return false; // accepted ou revoked
        if (iv.status && iv.status !== 'pending') return false;
        if (iv.expiresAt && new Date(iv.expiresAt) < now) return false;
        return true;
      });
      setPendingInvites(usable);
    } catch (e) {
      console.error('[invites/list]', e);
      setPendingInvites([]);
    } finally {
      setLoadingList(false);
    }
  };

  // Carrega lista ao abrir
  useEffect(() => {
    loadInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, clubId]);

  // Gera QR sempre que url mudar (lib qrcode → data URL PNG)
  useEffect(() => {
    if (!url) { setQrDataUrl(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const QRCode = (await import('qrcode')).default;
        const dataUrl = await QRCode.toDataURL(url, {
          width: 256,
          margin: 1,
          color: {
            dark: '#0A0806',
            light: '#F5F1EA',
          },
        });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (e) {
        console.error('[qr/generate]', e);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  const handleGenerate = async (opts: { maxUses?: number; expiresInDays?: number } = {}) => {
    if (!session) {
      toast.error('Sessão expirada. Faça login novamente.');
      return;
    }
    if (!clubId) {
      console.error('[invite] clubId undefined ao tentar gerar', { activeInvite, clubName });
      toast.error('Não consegui identificar o grupo. Volte pra página do grupo e tente novamente.');
      return;
    }
    setGenerating(true);
    try {
      const r = await api.createInvite(session, clubId, opts);
      setActiveInvite(r);
      await loadInvites(); // atualiza lista pra incluir o novo convite
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao gerar link');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Não consegui copiar — copie manualmente');
    }
  };

  const inviteMessage = `Vem ler comigo no Forjamente! ${clubName ? `${clubBookTitle ? `Estamos lendo "${clubBookTitle}" no ${T.s} ` : `Junte-se ao ${T.s} `}"${clubName}".` : ''} ${url}`;

  const handleNativeShare = async () => {
    if (!url) return;
    if ((navigator as any).share) {
      try {
        await (navigator as any).share({
          title: `Convite — ${clubName || 'Forjamente'}`,
          text: inviteMessage,
          url,
        });
      } catch (e) { /* user cancelou */ }
    } else {
      handleCopy();
    }
  };

  const handleWhatsApp = () => {
    if (!url) return;
    const text = encodeURIComponent(inviteMessage);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleRevoke = async (inviteId: string) => {
    if (!session || !clubId) return;
    if (!window.confirm('Revogar este convite? O link parará de funcionar.')) return;
    setRevokingId(inviteId);
    try {
      await api.revokeInvite(session, clubId, inviteId);
      // Se revogou o ativo, limpa
      if (activeInvite?.id === inviteId) setActiveInvite(null);
      await loadInvites();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao revogar');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div style={{
      width: '100%', height: '100%', position: 'relative',
      background: 'rgba(10,8,6,0.55)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'flex-end',
    }}>
      <div style={{
        width: '100%', maxWidth: 480, margin: '0 auto',
        background: 'var(--paper)',
        borderTopLeftRadius: 28, borderTopRightRadius: 28,
        padding: '14px 16px 36px',
        boxShadow: '0 -20px 60px rgba(0,0,0,0.25)',
        maxHeight: '92%', overflow: 'auto',
        boxSizing: 'border-box',
      }}>
        <div style={{ width: 36, height: 4, background: 'var(--ink-4)', opacity: 0.3, borderRadius: 2, margin: '0 auto 18px' }}/>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <div className="mono" style={{
              fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)',
            }}>
              Convidar
            </div>
            <div className="serif" style={{ fontSize: 28, lineHeight: 1.05, marginTop: 4 }}>
              Chame mais leitores
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'var(--paper-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="close" size={16}/>
          </button>
        </div>

        {/* Estado: nenhum convite ativo ainda → CTA pra gerar */}
        {!activeInvite && (
          <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{
              padding: 16, borderRadius: 14,
              border: '1px dashed var(--line-strong)',
              textAlign: 'center', color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.5,
            }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>🔗</div>
              Gere um link único pra convidar leitores. Você pode revogar a qualquer momento e criar novos.
            </div>
            <button
              onClick={() => handleGenerate({ expiresInDays: 30, maxUses: 100 })}
              disabled={generating}
              style={{
                padding: '14px 18px', borderRadius: 14,
                background: 'linear-gradient(135deg, var(--ember), #E07A3B)',
                color: 'var(--paper)', border: 'none',
                fontSize: 14, fontWeight: 500, cursor: 'pointer',
                boxShadow: '0 12px 24px -10px rgba(199,83,26,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {generating ? 'Gerando…' : <>🔗 Gerar link de convite</>}
            </button>
            <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-4)', textAlign: 'center', letterSpacing: '0.06em' }}>
              padrão: 30 dias · até 100 usos
            </div>
          </div>
        )}

        {/* Convite ativo: link, QR, share */}
        {activeInvite && (
        <>
        <div style={{
          marginTop: 20,
          background: 'var(--paper-2)',
          padding: 12, borderRadius: 16,
          border: '1px solid var(--line)',
        }}>
          <div className="mono" style={{
            fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
            color: 'var(--ink-3)', marginBottom: 8,
          }}>
            Pré-visualização
          </div>
          <div style={{ display: 'flex', gap: 10, padding: 10, background: 'var(--paper)', borderRadius: 12, minWidth: 0 }}>
            <div style={{
              width: 60, height: 60, borderRadius: 8,
              background: 'linear-gradient(135deg, var(--ember), #E07A3B)',
              flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <UsersIcon size={24} color="rgba(255,255,255,0.85)"/>
            </div>
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <div className="serif" style={{
                fontSize: 15, lineHeight: 1.15, color: 'var(--ink)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{clubName || 'Forjamente'}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                Você foi convidado para um {T.s} de leitura.
                {clubBookTitle && <> Estão lendo <i>"{clubBookTitle}"</i>.</>}
              </div>
              <div className="mono" style={{
                fontSize: 9, color: 'var(--ember)', marginTop: 4, letterSpacing: '0.04em',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}>
                {url}
              </div>
            </div>
          </div>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: '112px minmax(0, 1fr)',
          gap: 12, marginTop: 16,
        }}>
          <div style={{
            width: 112, height: 112, borderRadius: 16,
            background: 'var(--paper-2)',
            border: '1px solid var(--line)',
            padding: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxSizing: 'border-box',
          }}>
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="QR Code do convite" style={{ width: '100%', height: '100%', borderRadius: 8 }}/>
            ) : (
              <div className="mono" style={{ fontSize: 9, color: 'var(--ink-4)', textAlign: 'center' }}>gerando QR…</div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            <div style={{
              padding: '10px 12px',
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              borderRadius: 12, fontFamily: 'Geist Mono, monospace', fontSize: 10.5,
              color: 'var(--ink-2)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              letterSpacing: '0.04em',
              minWidth: 0, boxSizing: 'border-box',
            }}>
              {url.replace(/^https?:\/\//, '')}
            </div>
            <button onClick={handleCopy} style={{
              padding: '10px 12px', borderRadius: 12,
              background: copied ? 'var(--sage)' : 'var(--ink)',
              color: 'var(--paper)',
              fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'background 200ms',
              width: '100%', minWidth: 0, boxSizing: 'border-box',
            }}>
              <Icon name="check" size={14} color="var(--paper)"/>
              <span style={{ whiteSpace: 'nowrap' }}>{copied ? 'Copiado!' : 'Copiar link'}</span>
            </button>
            <div className="mono" style={{
              fontSize: 9.5, color: 'var(--ink-4)', letterSpacing: '0.06em',
              lineHeight: 1.3,
            }}>
              QR pra projetar em encontros presenciais
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 8, marginTop: 16 }}>
          <button onClick={handleWhatsApp} style={{
            padding: '12px 10px', borderRadius: 14,
            background: '#25D366', color: 'white', fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            border: 'none', cursor: 'pointer',
            minWidth: 0, boxSizing: 'border-box',
          }}>
            <span style={{ whiteSpace: 'nowrap' }}>💬 WhatsApp</span>
          </button>
          {(navigator as any).share ? (
            <button onClick={handleNativeShare} style={{
              padding: '12px 10px', borderRadius: 14,
              background: 'var(--ink)', color: 'var(--paper)',
              fontSize: 13, fontWeight: 500,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              border: 'none', cursor: 'pointer',
              minWidth: 0, boxSizing: 'border-box',
            }}>
              <span style={{ whiteSpace: 'nowrap' }}>📤 Compartilhar</span>
            </button>
          ) : (
            <button onClick={() => handleGenerate({ expiresInDays: 30, maxUses: 100 })} disabled={generating} style={{
              padding: '12px 10px', borderRadius: 14,
              background: 'transparent', border: '1px solid var(--line-strong)',
              color: 'var(--ink-3)', fontSize: 13, fontWeight: 500,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              cursor: 'pointer',
              minWidth: 0, boxSizing: 'border-box',
            }}>
              <span style={{ whiteSpace: 'nowrap' }}>🔄 Gerar novo</span>
            </button>
          )}
        </div>
        </>
        )}

        {/* Convites pendentes (lista admin pra revogar) */}
        {pendingInvites && pendingInvites.length > 0 && (
          <>
            <div className="mono" style={{
              fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
              color: 'var(--ink-3)', marginTop: 24, marginBottom: 10,
            }}>
              Convites ativos ({pendingInvites.length})
            </div>
            <div style={{
              background: 'var(--paper-2)', borderRadius: 14,
              border: '1px solid var(--line)', overflow: 'hidden',
            }}>
              {pendingInvites.map((iv: any, i: number) => (
                <div key={iv.id} style={{
                  padding: 12, display: 'flex', alignItems: 'center', gap: 8,
                  borderBottom: i < pendingInvites.length - 1 ? '1px solid var(--line)' : 'none',
                  minWidth: 0,
                }}>
                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <div className="mono" style={{
                      fontSize: 10, color: 'var(--ink-2)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}>
                      {(iv.url || '').replace(/^https?:\/\//, '')}
                    </div>
                    <div style={{
                      fontSize: 10.5, color: 'var(--ink-3)', marginTop: 2,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {iv.targetEmail ? `Para ${iv.targetEmail}` : 'link aberto'}
                      {iv.maxUses ? ` · até ${iv.maxUses} usos` : ''}
                      {iv.expiresAt ? ` · expira ${new Date(iv.expiresAt).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })}` : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => { setActiveInvite(iv); }}
                    style={{
                      padding: '5px 9px', borderRadius: 999,
                      background: 'transparent', border: '1px solid var(--line)',
                      fontSize: 10.5, color: 'var(--ink-3)', cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >mostrar</button>
                  <button
                    onClick={() => handleRevoke(iv.id)}
                    disabled={revokingId === iv.id}
                    style={{
                      padding: '5px 9px', borderRadius: 999,
                      background: 'transparent', border: '1px solid var(--ember)',
                      fontSize: 10.5, color: 'var(--ember)', cursor: 'pointer',
                      opacity: revokingId === iv.id ? 0.5 : 1,
                      flexShrink: 0,
                    }}
                  >{revokingId === iv.id ? '…' : 'revogar'}</button>
                </div>
              ))}
            </div>
          </>
        )}

        {!activeInvite && pendingInvites && pendingInvites.length === 0 && (
          <div className="mono" style={{
            fontSize: 10, color: 'var(--ink-4)', letterSpacing: '0.06em',
            textAlign: 'center', marginTop: 18,
          }}>
            nenhum convite ativo · gere o primeiro acima
          </div>
        )}
      </div>
    </div>
  );
};
